// User Management Service
// API calls for managing staff member accounts

import { acquireTokenSilent } from '../config/msalConfig';

export interface CreateUserRequest {
  email: string;
  role: 'admin' | 'clinician' | 'reception' | 'receptionist' | 'pa' | 'manager' | 'viewer';
  department?: string;
  // Legacy fields for backward compatibility (not used in SSO pre-registration)
  first_name?: string;
  last_name?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  organization_id?: string;
  display_name?: string;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  role?: 'admin' | 'clinician' | 'reception' | 'receptionist' | 'pa' | 'manager' | 'viewer' | 'pending';
  status?: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  department?: string;
  display_name?: string;
}

export interface UserWorkload {
  user_id: string;
  name: string;
  role: string;
  referral_count: number;
}

export interface CreateUserResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  role: string;
  status: string;
  organization_id?: string;
  department?: string;
  temporary_password?: string;  // Optional - not returned for SSO pre-registration
  needs_password_change?: boolean;  // Optional - not returned for SSO pre-registration
  created_at?: string;
  message?: string;  // Success message from backend
}

export interface UserListItem {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  role: string;
  status: string;
  department?: string;
  created_at: string;
}

export class UserService {
  private static baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  /**
   * Create a new staff member account (pre-register for SSO)
   * Requires admin role in JWT token
   */
  static async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to create user`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
          console.warn('Error creating user:', response);
          // Attach status code to error for better handling
          const errorWithStatus = new Error(message) as any;
          errorWithStatus.status = response.status;
          throw errorWithStatus;
        } catch (e) {
          // If JSON parsing failed, still throw with status
          if ((e as any).status) throw e;
          console.warn('Error response is not valid JSON:', response.status, response.statusText);
          const errorWithStatus = new Error(message) as any;
          errorWithStatus.status = response.status;
          throw errorWithStatus;
        }
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Get all staff members
   * Requires authentication
   */
  static async getUsers(): Promise<UserListItem[]> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/get-users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to fetch users`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      const data = await response.json();
      // Handle paginated response with users array
      return data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Update an existing staff member account
   * Requires admin role in JWT token
   */
  static async updateUser(userId: string, userData: UpdateUserRequest): Promise<UserListItem> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to update user`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
          console.warn('Error updating user:', response);
        } catch (e) {
          // Response body is not JSON, use generic message
          console.warn('Error response is not valid JSON:', response.status, response.statusText);
        }
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Get workload counts for Reception and PA users
   * Requires PA or Admin role
   */
  static async getUsersWorkload(): Promise<UserWorkload[]> {
    try {
      const token = await this.getToken();
      const response = await fetch(`${this.baseUrl}/users/workload`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to fetch user workload`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user workload:', error);
      throw error;
    }
  }

  /**
   * Get Entra ID access token via MSAL
   * Acquires token silently from MSAL cache
   */
  private static async getToken(): Promise<string> {
    try {
      const tokenResult = await acquireTokenSilent();
      return tokenResult.accessToken;
    } catch (error) {
      console.error('Failed to acquire access token:', error);
      return '';
    }
  }
}
